import type { SessionMessage } from '@/features/sessions/types/session.types'

export type ChatEventType = 'NEW_MESSAGE' | 'NEW_GROUP_MESSAGE'

/** Clave de un grupo de ganas de jugar tal como viaja en el evento (fecha `YYYY-MM-DD`). */
export interface ChatGroupRef {
  gameId: number
  cityCode: string
  windowStart: string
}

/**
 * Evento del stream SSE. Dos formas según `type` (el servidor omite los campos nulos):
 * `NEW_MESSAGE` (chat de partida: `sessionId` + `message`) y `NEW_GROUP_MESSAGE` (chat de
 * un grupo de ganas de jugar: `group` + `groupMessage`). Spec 2026-10-12.
 */
export type ChatStreamEvent =
  | { id: string; type: 'NEW_MESSAGE'; sessionId: number; message: SessionMessage }
  | { id: string; type: 'NEW_GROUP_MESSAGE'; group: ChatGroupRef; groupMessage: ChatMessage }

export type ChatStreamState =
  | 'CLOSED'
  | 'CONNECTING'
  | 'OPEN'
  | 'RECONNECTING'
  | 'POLLING_FALLBACK'
  | 'DISABLED'

/**
 * Mensaje de cualquier chat (partida o grupo). Un `SessionMessage` encaja tal cual; el de
 * grupo añade `kind` y, en el aviso del sistema `SESSION_CREATED`, `sessionId` (y
 * `content: null`).
 */
export interface ChatMessage {
  id: number
  userId: number
  username: string
  authorAvatarCode?: string | null
  authorRole?: 'USER' | 'ADMIN' | 'TIENDA'
  content: string | null
  createdAt: string
  kind?: 'USER' | 'SESSION_CREATED'
  /** Solo en `SESSION_CREATED`: la partida creada (`null` si ya no existe). */
  sessionId?: number | null
}

/**
 * De qué chat se habla: el de una partida o el de un grupo de ganas de jugar. Al de grupo
 * cada miembro entra por SUS ganas (`intentId`); `groupKey` identifica el grupo, que es lo
 * que trae el evento en tiempo real (ids de ganas distintos para cada miembro).
 */
export type ChatOrigin =
  | { kind: 'session'; sessionId: number }
  | { kind: 'group'; intentId: number; groupKey: string }

/** Clave estable de un grupo: juego + ciudad + inicio de franja. */
export function groupChatKey(group: ChatGroupRef): string {
  return `${group.gameId}:${group.cityCode}:${group.windowStart}`
}
